import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Checkbox,
  Button,
  Input,
  Row,
  Col,
  Card,
  Space,
  message,
} from "antd";
import { SearchOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://103.61.224.178:9876";

const Attendance = () => {
  const [form] = Form.useForm();
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [memberSearchText, setMemberSearchText] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
    fetchMembers();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/event`);
      if (res.data && Array.isArray(res.data.data)) {
        setEvents(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      message.error("Failed to fetch events.");
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/member/get-all-members`);
      if (res.data && Array.isArray(res.data.data)) {
        const activeMembers = res.data.data.filter((m) => !m.isDeleted);
        setMembers(activeMembers);
        setFilteredMembers(activeMembers);
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      message.error("Failed to fetch members.");
    }
  };

  // Load actual attendance first
  const fetchEventAttendance = async (eventId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/member/geteventattendance`);
      if (Array.isArray(res.data)) {
        const records = res.data
          .filter((r) => (r.eventid?._id === eventId || r.eventid === eventId))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const record = records[0];

        if (record) {
          // Actual attendance exists → load it
          setCurrentRecordId(record._id);
          const loadedMemberIds = (record.memberid || [])
            .map((m) => m._id || m)
            .filter((id) => members.some((mem) => mem._id === id));

          setSelectedMembers(loadedMemberIds);
          form.setFieldsValue({
            eventId: record.eventid._id || record.eventid,
            nonmember: record.nonmember || [],
          });
          message.info("Loaded existing attendance.");
          return true; // Found actual attendance
        }
      }
    } catch (err) {
      console.error("Error fetching attendance:", err);
    }

    // No actual attendance found
    setCurrentRecordId(null);
    return false;
  };

  // Load from pre-confirmation if no actual attendance
  const loadFromPreConfirmation = async (eventId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/member/geteventpreconfirm`);
      if (Array.isArray(res.data)) {
        const records = res.data
          .filter((r) => (r.eventid?._id === eventId || r.eventid === eventId))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const record = records[0];

        if (record) {
          const loadedMemberIds = (record.memberid || [])
            .map((m) => m._id || m)
            .filter((id) => members.some((mem) => mem._id === id));

          setSelectedMembers(loadedMemberIds);
          form.setFieldsValue({
            nonmember: record.nonmember || [],
          });
          message.success("Auto-loaded from pre-confirmation!");
          return true;
        } else {
          message.info("No pre-confirmation found. Starting fresh.");
        }
      }
    } catch (err) {
      console.error("Error loading pre-confirmation:", err);
      message.error("Failed to load pre-confirmation data.");
    }
    return false;
  };

  // Main handler when event is selected
  const handleEventChange = async (eventId) => {
    setSelectedEvent(eventId);
    form.setFieldsValue({ eventId });

    // Reset first
    setSelectedMembers([]);
    form.setFieldsValue({ nonmember: [] });
    setCurrentRecordId(null);

    if (members.length === 0) {
      // Wait for members to load
      const interval = setInterval(async () => {
        if (members.length > 0) {
          clearInterval(interval);
          await loadDataForEvent(eventId);
        }
      }, 100);
    } else {
      await loadDataForEvent(eventId);
    }
  };

  // Load priority: Actual Attendance → Pre-Confirmation → Fresh
  const loadDataForEvent = async (eventId) => {
    const hasActual = await fetchEventAttendance(eventId);
    if (!hasActual) {
      await loadFromPreConfirmation(eventId);
    }
  };

  // Search filter
  useEffect(() => {
    const filtered = members.filter((m) =>
      m.Name.toLowerCase().includes(memberSearchText.toLowerCase())
    );
    setFilteredMembers(filtered);
    setSelectAll(
      filtered.length > 0 && filtered.every((m) => selectedMembers.includes(m._id))
    );
  }, [memberSearchText, members, selectedMembers]);

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setSelectedMembers(checked ? filteredMembers.map((m) => m._id) : []);
  };

  const handleMemberSelect = (checked, memberId) => {
    const updated = checked
      ? [...selectedMembers, memberId]
      : selectedMembers.filter((id) => id !== memberId);
    setSelectedMembers(updated);
    setSelectAll(filteredMembers.length > 0 && updated.length === filteredMembers.length);
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        eventid: values.eventId,
        memberid: selectedMembers,
        nonmember: values.nonmember || [],
      };

      if (currentRecordId) {
        await axios.put(
          `${API_BASE_URL}/member/updateeventattendance/${currentRecordId}`,
          payload
        );
        message.success("Attendance updated successfully");
      } else {
        const res = await axios.post(`${API_BASE_URL}/member/eventattendance`, payload);
        message.success("Attendance saved successfully");
        if (res.data?.data?._id) {
          setCurrentRecordId(res.data.data._id);
        }
      }

      // Reload fresh data
      await fetchEventAttendance(values.eventId);
    } catch (err) {
      console.error("Error saving attendance:", err);
      message.error(err.response?.data?.message || "Failed to save attendance.");
    }
  };

  return (
    <Row gutter={[24, 24]} style={{ padding: 16 }}>
      <Col span={24}>
        <Card
          title="Event Attendance Form"
          style={{ borderRadius: 8 }}
          extra={
            <Space>
              <Button type="primary" onClick={() => navigate("/dashboard/atten-list")}>
                View List
              </Button>
            </Space>
          }
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="eventId"
              label="Select Event"
              rules={[{ required: true, message: "Please select an event" }]}
            >
              <Select
                placeholder="Choose Event"
                showSearch
                optionFilterProp="children"
                onChange={handleEventChange}
                value={selectedEvent}
              >
                {events.map((event) => (
                  <Option key={event._id} value={event._id}>
                    {event.eventName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedEvent && (
              <>
                <Form.Item label="Mark Attendees (Members)">
                  <Input
                    placeholder="Search members..."
                    prefix={<SearchOutlined />}
                    value={memberSearchText}
                    onChange={(e) => setMemberSearchText(e.target.value)}
                    style={{ width: 300, marginBottom: 16 }}
                  />
                  <Checkbox checked={selectAll} onChange={handleSelectAll}>
                    Select All Visible
                  </Checkbox>

                  <div
                    style={{
                      maxHeight: 350,
                      overflowY: "auto",
                      marginTop: 12,
                      padding: 12,
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      backgroundColor: "#fafafa",
                    }}
                  >
                    {filteredMembers.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#999", padding: 20 }}>
                        No members found
                      </div>
                    ) : (
                      filteredMembers.map((member) => (
                        <div
                          key={member._id}
                          style={{ display: "flex", alignItems: "center", padding: "6px 0" }}
                        >
                          <Checkbox
                            checked={selectedMembers.includes(member._id)}
                            onChange={(e) => handleMemberSelect(e.target.checked, member._id)}
                          />
                          <span style={{ marginLeft: 12, fontSize: 15 }}>{member.Name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </Form.Item>

                <Form.Item label="Non-Members (Guests)">
                  <Form.List name="nonmember">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Space key={key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
                            <Form.Item
                              {...rest}
                              name={[name, "name"]}
                              rules={[{ required: true, message: "Name required" }]}
                            >
                              <Input placeholder="Name" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, "mobilenumber"]}
                              rules={[
                                { required: true, message: "Mobile required" },
                                { pattern: /^\d{10}$/, message: "Must be 10 digits" },
                              ]}
                            >
                              <Input placeholder="Mobile" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, "address"]}
                              rules={[{ required: true, message: "Address required" }]}
                            >
                              <Input placeholder="Address" />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(name)} />
                          </Space>
                        ))}
                        <Form.Item>
                          <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                            Add Guest
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              </>
            )}

            <Form.Item>
              <Button type="primary" htmlType="submit" size="large">
                {currentRecordId ? "Update Attendance" : "Save Attendance"}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default Attendance;